import { formatHumanName, getExtensionValue, MedplumClient, resolveId } from "@medplum/core";
import {
  Appointment,
  AppointmentParticipant,
  Patient,
  Practitioner,
  QuestionnaireResponse,
  Reference,
} from "@medplum/fhirtypes";

import * as Sentry from "@sentry/react";
import { format, isValid, parse, parseISO, startOfDay } from "date-fns";
import chunk from "lodash/chunk";

import {
  ELIGIBILITY_LIST_EXCLUDED_TAG_TOKEN,
  ELIGIBILITY_LIST_STATUS_EXCLUDED_CODE,
  ELIGIBILITY_LIST_STATUS_SYSTEM,
} from "@/billing/constants/eligibility-list-status";
import {
  COVERAGE_END_DATE_LINK_ID,
  COVERAGE_GROUP_LINK_ID,
  COVERAGE_STATUS_LINK_ID,
  CoverageStatus,
} from "@/billing/constants/insurance-verification";
import {
  fetchVerificationResponsesForClient,
  getInsuranceSnapshotFromVerificationResponse,
  getVerificationResponseForTier,
} from "@/billing/services/insurance-verification";
import type { EligibilityAppointmentDateRange } from "@/billing/types/eligibility-appointment-date-range";
import { getPatientMrn } from "@/clients/utils/mrn";
import { Extensions } from "@/fhir/enums/extensions";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import {
  BILLING_PAYMENT_METHOD_LABEL_INSURANCE,
  BILLING_PAYMENT_METHOD_LABEL_SELF_PAY,
  BillingPaymentMethodKind,
  resolveBillingPaymentMethodFromIntake,
} from "@/shared/client-billing-payment-method";
import { getCodeableConceptDisplay, joinReferenceString, setExtensionValue } from "@/utils/medplum";
import { fetchAllPaginatedResources } from "@/utils/pagination";

/** Visible rows per eligibility table page after filter and sort. */
export const ELIGIBILITY_PAGE_SIZE = 20;
/** Shared empty-cell placeholder for eligibility table fields. */
export const EMPTY_CELL_DISPLAY = "—";
/** Max patients per QuestionnaireResponse search so request URLs stay within length limits. */
export const INTAKE_PAYMENT_METHOD_PATIENT_CHUNK_SIZE = 50;

export interface EligibilityTableRow {
  appointmentId: string;
  clientId: string;
  clientName: string;
  /** Patient medical record number, or "—" when missing. */
  medicalRecordNumber: string;
  upcomingAppointment: string;
  appointmentType: string;
  provider: string;
  /** Self-Pay or Insurance from intake, or "—" when missing. */
  paymentMethod: string;
  insurance: string;
  /** Coverage end date from the primary verification response, shown as MM/dd/yyyy. */
  coverageEndDate: string;
  lastVerified: string;
  coverageStatus: CoverageStatus | undefined;
  primaryVerificationResponse: QuestionnaireResponse | undefined;
}

/** Result of building eligibility table rows for a practice date window. */
export interface EligibilityTableFetchResult {
  rows: EligibilityTableRow[];
}

interface EligibilityTablePrefetch {
  patientsById: Map<string, Patient>;
  practitionersByReference: Map<string, Practitioner>;
  verificationByPatientId: Map<string, QuestionnaireResponse[]>;
  paymentMethodByPatientId: Map<string, string>;
}

/** Stable key for a table row (appointment + client + time). */
export function getEligibilityTableRowKey(row: EligibilityTableRow): string {
  return `${row.appointmentId}-${row.clientId}-${row.upcomingAppointment}`;
}

/**
 * Returns the eligibility payment-method label from a client-intake QuestionnaireResponse.
 *
 * @param questionnaireResponse Latest client-intake response for the patient, if any
 * @returns "Self-Pay", "Insurance", or "—" when the answer is missing
 */
export function getPaymentMethodDisplayFromIntake(
  questionnaireResponse: QuestionnaireResponse | undefined,
): string {
  const resolved = resolveBillingPaymentMethodFromIntake(questionnaireResponse);
  if (resolved.kind === BillingPaymentMethodKind.SelfPay) {
    return BILLING_PAYMENT_METHOD_LABEL_SELF_PAY;
  }
  if (resolved.kind === BillingPaymentMethodKind.Insurance) {
    return BILLING_PAYMENT_METHOD_LABEL_INSURANCE;
  }
  return EMPTY_CELL_DISPLAY;
}

/**
 * Returns the coverage end date from a primary-tier verification QuestionnaireResponse.
 *
 * @param response Primary-tier verification response, if any
 * @returns End date as MM/dd/yyyy, or "—" when the answer is missing
 */
export function getCoverageEndDateFromResponse(
  response: QuestionnaireResponse | undefined,
): string {
  if (!response) return EMPTY_CELL_DISPLAY;
  const coverageGroup = response.item?.find((item) => item.linkId === COVERAGE_GROUP_LINK_ID);
  const coverageEndItem = coverageGroup?.item?.find(
    (item) => item.linkId === COVERAGE_END_DATE_LINK_ID,
  );
  const answer = coverageEndItem?.answer?.[0];
  if (!answer) return EMPTY_CELL_DISPLAY;
  const value = (answer as { valueDate?: string }).valueDate;
  return value ? format(parseISO(value), "MM/dd/yyyy") : EMPTY_CELL_DISPLAY;
}

/**
 * Returns true when a coverage end date (MM/dd/yyyy) is before today.
 * Returns false when the value is missing or invalid.
 */
export function isPastEligibilityCoverageEndDate(
  coverageEndDate: string,
  now = new Date(),
): boolean {
  if (coverageEndDate === EMPTY_CELL_DISPLAY) return false;
  const parsed = parse(coverageEndDate, "MM/dd/yyyy", now);
  if (!isValid(parsed)) return false;
  return startOfDay(parsed) < startOfDay(now);
}

function getAppointmentTypeDisplay(appointment: Appointment): string {
  const types = appointment.appointmentType;
  const concept = Array.isArray(types) ? types[0] : types;
  return getCodeableConceptDisplay(concept, 0, EMPTY_CELL_DISPLAY);
}

function getCoverageStatusFromResponse(
  response: QuestionnaireResponse | undefined,
): CoverageStatus | undefined {
  if (!response?.item) return undefined;
  const coverageGroup = response.item.find((item) => item.linkId === COVERAGE_GROUP_LINK_ID);
  const statusItem = coverageGroup?.item?.find((item) => item.linkId === COVERAGE_STATUS_LINK_ID);
  const answer = statusItem?.answer?.[0];
  if (!answer) return undefined;
  const code = (answer as { valueCoding?: { code?: string } }).valueCoding?.code;
  if (code === CoverageStatus.Active || code === CoverageStatus.NotEligible) return code;
  const display = (
    answer as { valueCoding?: { display?: string } }
  ).valueCoding?.display?.toLowerCase();
  if (display === "active") return CoverageStatus.Active;
  if (display?.includes("not eligible")) {
    return CoverageStatus.NotEligible;
  }
  return undefined;
}

function getParticipantByResourceType(
  appointment: Appointment,
  resourceType: "Patient" | "Practitioner",
): AppointmentParticipant | undefined {
  const prefix = `${resourceType}/`;
  return appointment.participant?.find((participant) =>
    participant.actor?.reference?.startsWith(prefix),
  );
}

/**
 * Fetches every appointment for a practice in the given inclusive date window.
 * Omits cancelled appointments and appointments tagged as removed from the list.
 * Appointments excluded only via the eligibility-list extension are still returned
 * and dropped by `isAppointmentExcludedFromEligibilityList`.
 */
async function fetchUpcomingAppointmentsForPractice(
  medplum: MedplumClient,
  organizationId: string,
  dateRange: EligibilityAppointmentDateRange,
): Promise<Appointment[]> {
  const searchedAppointments = await fetchAllPaginatedResources<Appointment>(
    medplum,
    "Appointment",
    {
      date: `ge${dateRange.startDate.toISOString()}`,
      end: `le${dateRange.endDate.toISOString()}`,
      "location.organization": joinReferenceString("Organization", organizationId),
      "status:not": "cancelled",
      "_tag:not": ELIGIBILITY_LIST_EXCLUDED_TAG_TOKEN,
      _sort: "date",
    },
  );
  if (!searchedAppointments.success) {
    throw searchedAppointments.error;
  }
  return searchedAppointments.data;
}

function isAppointmentExcludedFromEligibilityList(appointment: Appointment): boolean {
  const hasExcludedTag = appointment.meta?.tag?.some(
    (tag) =>
      tag.system === ELIGIBILITY_LIST_STATUS_SYSTEM &&
      tag.code === ELIGIBILITY_LIST_STATUS_EXCLUDED_CODE,
  );
  if (hasExcludedTag) return true;
  const value = getExtensionValue(appointment, Extensions.AppointmentExcludedFromEligibilityList);
  return value === true || value === "true";
}

/**
 * Returns a copy of the appointment with the eligibility-list excluded tag, preserving other tags.
 */
function withEligibilityListExcludedTag(appointment: Appointment): Appointment {
  const preservedTags =
    appointment.meta?.tag?.filter((tag) => tag.system !== ELIGIBILITY_LIST_STATUS_SYSTEM) ?? [];

  return {
    ...appointment,
    meta: {
      ...appointment.meta,
      tag: [
        ...preservedTags,
        {
          system: ELIGIBILITY_LIST_STATUS_SYSTEM,
          code: ELIGIBILITY_LIST_STATUS_EXCLUDED_CODE,
        },
      ],
    },
  };
}

/**
 * Marks an appointment as excluded from the practice billing eligibility list.
 * Persists the searchable excluded tag and the exclusion extension.
 */
export async function excludeAppointmentFromEligibilityList(
  medplum: MedplumClient,
  appointmentId: string,
): Promise<void> {
  const appointment = await medplum.readResource("Appointment", appointmentId);
  const extension = setExtensionValue(
    appointment.extension ?? [],
    Extensions.AppointmentExcludedFromEligibilityList,
    { valueBoolean: true },
  );
  const taggedAppointment = withEligibilityListExcludedTag({ ...appointment, extension });
  await medplum.updateResource(taggedAppointment);
}

/**
 * Loads the latest intake payment-method label for each patient in a practice.
 * Searches patients in chunks inside the organization compartment.
 * On chunk failure, logs to Sentry and continues so later patients can still load.
 */
async function prefetchPaymentMethodByPatientId(
  medplum: MedplumClient,
  organizationId: string,
  patientIds: string[],
): Promise<Map<string, string>> {
  const paymentMethodByPatientId = new Map<string, string>();
  if (patientIds.length === 0) return paymentMethodByPatientId;

  const patientIdSet = new Set(patientIds);
  const patientIdChunks = chunk(patientIds, INTAKE_PAYMENT_METHOD_PATIENT_CHUNK_SIZE);
  const organizationCompartment = joinReferenceString("Organization", organizationId);

  for (const patientIdChunk of patientIdChunks) {
    const questionnaireResponseResult = await fetchAllPaginatedResources<QuestionnaireResponse>(
      medplum,
      "QuestionnaireResponse",
      {
        _compartment: organizationCompartment,
        patient: patientIdChunk
          .map((patientId) => joinReferenceString("Patient", patientId))
          .join(","),
        identifier: Questionnaires.ClientIntake,
        _sort: "-_lastUpdated",
      },
    );

    if (!questionnaireResponseResult.success) {
      Sentry.captureException(questionnaireResponseResult.error);
      continue;
    }

    questionnaireResponseResult.data.reduce((paymentMethods, questionnaireResponse) => {
      const patientId = resolveId(questionnaireResponse.subject);
      if (!patientId || !patientIdSet.has(patientId) || paymentMethods.has(patientId)) {
        return paymentMethods;
      }
      paymentMethods.set(patientId, getPaymentMethodDisplayFromIntake(questionnaireResponse));
      return paymentMethods;
    }, paymentMethodByPatientId);
  }

  return paymentMethodByPatientId;
}

function collectUniquePatientIds(appointments: Appointment[]): string[] {
  return [
    ...appointments.reduce((patientIds, appointment) => {
      const patientId = resolveId(getParticipantByResourceType(appointment, "Patient")?.actor);
      if (patientId) patientIds.add(patientId);
      return patientIds;
    }, new Set<string>()),
  ];
}

function collectUniquePractitionerReferences(appointments: Appointment[]): string[] {
  return [
    ...appointments.reduce((practitionerReferences, appointment) => {
      const reference = getParticipantByResourceType(appointment, "Practitioner")?.actor?.reference;
      if (reference) practitionerReferences.add(reference);
      return practitionerReferences;
    }, new Set<string>()),
  ];
}

function buildRowFromPrefetch(
  appointment: Appointment,
  prefetch: EligibilityTablePrefetch,
): EligibilityTableRow | null {
  const patientId = resolveId(getParticipantByResourceType(appointment, "Patient")?.actor);
  if (!patientId) return null;

  const patient = prefetch.patientsById.get(patientId);
  if (!patient) return null;

  const practitionerReference = getParticipantByResourceType(appointment, "Practitioner")?.actor
    ?.reference;
  const practitioner = practitionerReference
    ? prefetch.practitionersByReference.get(practitionerReference)
    : undefined;
  const providerDisplay = practitioner
    ? (formatHumanName(practitioner.name?.[0]) ?? EMPTY_CELL_DISPLAY)
    : EMPTY_CELL_DISPLAY;

  const clientName = formatHumanName(patient.name?.[0]) ?? EMPTY_CELL_DISPLAY;
  const medicalRecordNumber = getPatientMrn(patient) ?? EMPTY_CELL_DISPLAY;
  const appointmentStart = appointment.start;
  const upcomingAppointment = appointmentStart
    ? format(parseISO(appointmentStart), "MM/dd/yyyy 'at' h:mm a")
    : EMPTY_CELL_DISPLAY;

  const responses = prefetch.verificationByPatientId.get(patientId) ?? [];
  const primaryVerificationResponse = getVerificationResponseForTier(responses, "primary");
  const coverageStatus = getCoverageStatusFromResponse(primaryVerificationResponse);
  const paymentMethod = prefetch.paymentMethodByPatientId.get(patientId) ?? EMPTY_CELL_DISPLAY;

  const insuranceCompany = primaryVerificationResponse
    ? getInsuranceSnapshotFromVerificationResponse(primaryVerificationResponse)?.insuranceCompany
    : undefined;
  const lastVerifiedUpdated = primaryVerificationResponse?.meta?.lastUpdated;

  return {
    appointmentId: appointment.id ?? "",
    clientId: patientId,
    clientName,
    medicalRecordNumber,
    upcomingAppointment,
    appointmentType: getAppointmentTypeDisplay(appointment),
    provider: providerDisplay,
    paymentMethod,
    insurance: insuranceCompany?.trim() || EMPTY_CELL_DISPLAY,
    coverageEndDate: getCoverageEndDateFromResponse(primaryVerificationResponse),
    lastVerified: lastVerifiedUpdated
      ? format(new Date(lastVerifiedUpdated), "MM/dd/yyyy")
      : EMPTY_CELL_DISPLAY,
    coverageStatus,
    primaryVerificationResponse,
  };
}

/**
 * Builds eligibility table rows for every appointment in a practice date window.
 * Omits appointments marked as removed from the eligibility list.
 * Prefetches patients, practitioners, verification responses, and intake payment methods in
 * parallel, once per unique patient or practitioner in the window.
 */
export async function fetchEligibilityTableRows(
  medplum: MedplumClient,
  organizationId: string,
  dateRange: EligibilityAppointmentDateRange,
): Promise<EligibilityTableFetchResult> {
  const appointments = await fetchUpcomingAppointmentsForPractice(
    medplum,
    organizationId,
    dateRange,
  );
  const includedAppointments = appointments.filter(
    (appointment) => !isAppointmentExcludedFromEligibilityList(appointment),
  );

  const uniquePatientIds = collectUniquePatientIds(includedAppointments);
  const uniquePractitionerReferences = collectUniquePractitionerReferences(includedAppointments);

  const [patientResults, practitionerResults, verificationResults, paymentMethodByPatientId] =
    await Promise.all([
      Promise.allSettled(
        uniquePatientIds.map((patientId) => medplum.readResource("Patient", patientId)),
      ),
      Promise.allSettled(
        uniquePractitionerReferences.map((reference) =>
          medplum.readReference({ reference } as Reference<Practitioner>),
        ),
      ),
      Promise.all(
        uniquePatientIds.map((patientId) =>
          fetchVerificationResponsesForClient(medplum, patientId),
        ),
      ),
      prefetchPaymentMethodByPatientId(medplum, organizationId, uniquePatientIds),
    ]);

  const patientsById = uniquePatientIds.reduce((patients, patientId, index) => {
    const result = patientResults[index];
    if (result?.status === "fulfilled") patients.set(patientId, result.value);
    return patients;
  }, new Map<string, Patient>());

  const practitionersByReference = uniquePractitionerReferences.reduce(
    (practitioners, reference, index) => {
      const result = practitionerResults[index];
      if (result?.status === "fulfilled") practitioners.set(reference, result.value);
      return practitioners;
    },
    new Map<string, Practitioner>(),
  );

  const verificationByPatientId = uniquePatientIds.reduce((verifications, patientId, index) => {
    verifications.set(patientId, verificationResults[index] ?? []);
    return verifications;
  }, new Map<string, QuestionnaireResponse[]>());

  const prefetch: EligibilityTablePrefetch = {
    patientsById,
    practitionersByReference,
    verificationByPatientId,
    paymentMethodByPatientId,
  };

  const rows = includedAppointments.reduce<EligibilityTableRow[]>((tableRows, appointment) => {
    const row = buildRowFromPrefetch(appointment, prefetch);
    if (row) tableRows.push(row);
    return tableRows;
  }, []);

  return { rows };
}
